import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { uiStyles } from '../theme/uiStyles';
import { slugify, toArray } from '../utils/slugify';
import { apiFetch } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { DEMO_PORTFOLIOS, demoStats } from '../data/demoPortfolios';

/* ── Demo view for non-authenticated visitors ── */
function readCachedStats(id) {
  try {
    const raw = sessionStorage.getItem(`demo-stats-${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function DemoPortfoliosView() {
  const [quotes, setQuotes] = useState(null);
  // Map of id → cached exact stats (written by the detail page after render).
  // Read once on mount so the cards immediately match whatever the visitor
  // saw on the detail page last time.
  const [cached] = useState(() =>
    Object.fromEntries(DEMO_PORTFOLIOS.map(p => [p.id, readCachedStats(p.id)]))
  );

  // One batch fetch of live current prices for every demo ticker — used for
  // any portfolio that doesn't have a cached snapshot yet.
  useEffect(() => {
    const tickers = Array.from(new Set(
      DEMO_PORTFOLIOS.flatMap(p => p.holdings.map(h => h.yahoo))
    ));
    if (tickers.length === 0) return;
    let cancelled = false;
    apiFetch(`/api/stock/quotes?tickers=${encodeURIComponent(tickers.join(','))}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setQuotes(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <h1 style={titleStyle}>Portfolios</h1>
      </div>

      {/* Sign-in prompt */}
      <div style={demoBannerStyle}>
        <div style={demoBannerText}>
          <strong>Sign in to manage your own portfolios.</strong>
          <span> Track your holdings, P&amp;L, dividends and more.</span>
        </div>
        <div style={demoBannerActions}>
          <Link to="/login"    style={demoBtnPrimary}>Sign in</Link>
          <Link to="/register" style={demoBtnOutline}>Create account</Link>
        </div>
      </div>

      {/* Demo portfolio cards — clickable */}
      {DEMO_PORTFOLIOS.map((p) => {
        // Prefer the exact snapshot the detail page wrote into sessionStorage
        // last time the visitor opened this portfolio; fall back to a live
        // quote-based approximation otherwise.
        const st = cached[p.id] || demoStats(p, quotes);
        const pnlColor = st.pnl >= 0 ? '#22c55e' : '#ef4444';
        return (
          <Link key={p.id} to={`/portfolios/demo/${p.id}`} style={portfolioCardStyle}>
            <div style={demoLabelStyle}>DEMO</div>
            <div style={cardHeadStyle}>
              <div>
                <div style={portfolioNameStyle}>{p.name}</div>
                <div style={portfolioMetaStyle}>Base Currency: {p.currency}</div>
              </div>
              <div style={currencyBadgeStyle}>{p.currency}</div>
            </div>
            <div style={statRowStyle}>
              <Stat label="Value"          value={fmtMoney(st.value, p.currency)} />
              <Stat label="Total P/L"      value={fmtMoney(st.pnl, p.currency)}      color={pnlColor} />
              <Stat label="Total P/L %"    value={fmtPct(st.pnlPct)}                 color={pnlColor} />
              <Stat label="Realized P/L"   value={fmtMoney(st.realizedPnl, p.currency)} />
              <Stat label="Open positions" value={String(st.positions)} />
            </div>
          </Link>
        );
      })}

      <p style={demoNoteStyle}>
        These are sample portfolios for illustration. Sign in to create and track your own.
      </p>
    </div>
  );
}

const demoBannerStyle = {
  background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '14px',
  padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center',
  justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
};
const demoBannerText    = { fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.5 };
const demoBannerActions = { display: 'flex', gap: '10px', flexShrink: 0 };
const demoBtnPrimary    = { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'var(--accent)', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '14px' };
const demoBtnOutline    = { display: 'inline-flex', alignItems: 'center', height: '36px', padding: '0 18px', borderRadius: '10px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 600, fontSize: '14px' };
const demoLabelStyle    = { display: 'inline-block', background: 'var(--accent)', color: '#fff', fontSize: '10px', fontWeight: 800, letterSpacing: '0.8px', borderRadius: '6px', padding: '3px 8px', marginBottom: '12px' };
const demoNoteStyle     = { fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '8px' };

function PortfoliosPage() {
  const { user } = useAuth();

  const [portfolios, setPortfolios] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPortfolios = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await apiFetch('/api/portfolios');

      if (!res.ok) {
        throw new Error('Failed to load portfolios.');
      }

      const data = toArray(await res.json());
      setPortfolios(data);

      // Fetch summaries in parallel (best-effort — silently ignore individual failures)
      const summaries = await Promise.all(
        data.map(async (p) => {
          try {
            const r = await apiFetch(`/api/portfolio-v2/summary?portfolioId=${p.id}`);
            if (!r.ok) return [p.id, null];
            return [p.id, await r.json()];
          } catch {
            return [p.id, null];
          }
        })
      );
      const map = {};
      summaries.forEach(([id, s]) => { map[id] = s; });
      setSummaries(map);
    } catch (err) {
      setError(err.message || 'Failed to load portfolios.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPortfolios();
  }, []);

  // Show demo view for unauthenticated visitors (after all hooks)
  if (!user) return <DemoPortfoliosView />;

  const maxPortfolios = user?.maxPortfolios ?? 3;
  const canCreateMore = portfolios.length < maxPortfolios;

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <h1 style={titleStyle}>Portfolios</h1>
        <div style={headerRightStyle}>
          <div style={countBadgeStyle}>{portfolios.length} / {maxPortfolios}</div>
          {canCreateMore && (
            <Link to="/portfolios/new" style={addCardStyle}>
              <div style={plusIconStyle}>+</div>
              <div style={addLabelStyle}>New portfolio</div>
            </Link>
          )}
        </div>
      </div>

      {error && <div style={uiStyles.errorBox}>{error}</div>}

      {loading ? (
        <section style={cardStyle}><p style={textStyle}>Loading portfolios...</p></section>
      ) : portfolios.length === 0 ? (
        <section style={cardStyle}><p style={textStyle}>No portfolios yet.</p></section>
      ) : (
        portfolios.map((portfolio) => {
          const summary  = summaries[portfolio.id];
          const currency = (summary?.baseCurrency || portfolio.baseCurrency || 'USD').toUpperCase();
          const walletOn = Boolean(portfolio?.walletEnabled);
          // Wallet-off portfolios don't track cash → totalValueBase includes a
          // bogus contributions-minus-costs cash field, so use holdingsValueBase.
          const value    = summary
            ? (walletOn
                ? Number(summary.totalValueBase ?? summary.holdingsValueBase ?? 0)
                : Number(summary.holdingsValueBase ?? 0))
            : null;
          const pnl      = summary ? Number(summary.totalPnlBase ?? 0) : null;
          const realized = summary ? Number(summary.realizedPnlBase ?? 0) : null;
          // P/L % — same approach as the dashboard: sum each open holding's
          // costBasisOpenBase. Works for both wallet-on and wallet-off.
          const costBasis = Array.isArray(summary?.holdings)
            ? summary.holdings.reduce((s, h) => s + Number(h?.costBasisOpenBase || 0), 0)
            : 0;
          const pnlPct   = pnl != null && costBasis > 0 ? (pnl / costBasis) * 100 : null;
          // Yield on Cost = forward annual dividend ÷ cost basis × 100
          const annualDividendForecast = Array.isArray(summary?.holdings)
            ? summary.holdings.reduce((s, h) => {
                const annual = Number(h?.annualDividendTotal || 0);
                const native = Number(h?.marketValueNative || 0);
                const base   = Number(h?.marketValueBase || 0);
                return annual > 0 && native > 0 ? s + annual * (base / native) : s;
              }, 0)
            : 0;
          const yieldOnCost = costBasis > 0 ? (annualDividendForecast / costBasis) * 100 : null;
          const positions = Array.isArray(summary?.holdings)
            ? summary.holdings.filter(h => Number(h.openQuantity ?? h.quantity ?? 0) > 0).length
            : null;
          const cash     = walletOn && summary ? Number(summary.cashBalanceBase ?? 0) : null;
          const pnlColor = pnl == null ? 'var(--text-secondary)' : pnl >= 0 ? '#22c55e' : '#ef4444';

          return (
            <Link
              key={portfolio.id}
              to={'/portfolio/' + slugify(portfolio.name)}
              style={portfolioCardStyle}
            >
              <div style={cardHeadStyle}>
                <div>
                  <div style={portfolioNameStyle}>{portfolio.name}</div>
                  <div style={portfolioMetaStyle}>Base Currency: {currency}</div>
                </div>
                <div style={currencyBadgeStyle}>{currency}</div>
              </div>

              <div style={statRowStyle}>
                <Stat label="Value"           value={fmtMoney(value, currency)} />
                <Stat label="Total P/L"       value={fmtMoney(pnl, currency)}        color={pnlColor} />
                <Stat label="Total P/L %"     value={fmtPct(pnlPct)}                 color={pnlColor} />
                <Stat label="Realized P/L"    value={fmtMoney(realized, currency)} />
                {walletOn && (
                  <Stat label="Cash"          value={fmtMoney(cash, currency)} />
                )}
                <Stat label="Open positions"  value={positions != null ? String(positions) : '—'} />
                <Stat label="Yield on Cost"   value={fmtPctUnsigned(yieldOnCost)} />
              </div>
            </Link>
          );
        })
      )}

      {!canCreateMore && (
        <section style={cardStyle}>
          <p style={textStyle}>You've reached the maximum of {maxPortfolios} portfolios.</p>
        </section>
      )}
    </div>
  );
}

const pageStyle = {
  maxWidth: '1100px',
  width: '100%',
  margin: '0 auto',
  padding: '20px 0 40px',
  boxSizing: 'border-box',
};

const titleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
};

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
};

const headerRightStyle = { display: 'flex', alignItems: 'center', gap: '12px' };

const addCardStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  height: '34px', boxSizing: 'border-box',
  background: 'var(--surface)',
  border: '2px dashed var(--border)',
  borderRadius: '999px',
  padding: '0 14px 0 6px',
  textDecoration: 'none',
  color: 'var(--text-secondary)',
  transition: 'border-color 0.15s, color 0.15s',
};

const plusIconStyle = {
  fontSize: '16px', fontWeight: 400, lineHeight: 1,
  width: '22px', height: '22px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '1.5px solid currentColor', borderRadius: '50%',
};

const addLabelStyle = { fontSize: '13px', fontWeight: 700 };

const portfolioCardStyle = {
  display: 'block',
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  textDecoration: 'none',
  color: 'var(--text-primary)',
};

const portfolioNameStyle = {
  fontWeight: 800,
  fontSize: '18px',
  marginBottom: '6px',
};

const portfolioMetaStyle = {
  color: 'var(--text-secondary)',
  fontSize: '14px',
};

const textStyle = {
  color: 'var(--text-primary)',
};

function Stat({ label, value, color }) {
  return (
    <div style={statBoxStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={{ ...statValueStyle, color: color || 'var(--text-primary)' }}>{value ?? '—'}</div>
    </div>
  );
}

function fmtMoney(v, currency) {
  if (v == null || Number.isNaN(v)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${v.toFixed(2)} ${currency}`;
  }
}

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

function fmtPctUnsigned(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}%`;
}

const headerRowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: '12px', marginBottom: '24px',
};

const countBadgeStyle = {
  display: 'inline-flex', alignItems: 'center',
  height: '34px', boxSizing: 'border-box',
  background: 'var(--surface-strong)', border: '1px solid var(--border)',
  borderRadius: '999px', padding: '0 14px',
  fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)',
};

const cardHeadStyle = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  gap: '12px', marginBottom: '20px',
};

const currencyBadgeStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '8px', padding: '4px 10px',
  fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)',
  letterSpacing: '0.5px',
};

const statRowStyle = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  gap: '12px',
};

const statBoxStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '10px', padding: '10px 12px',
};

const statLabelStyle = { fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' };
const statValueStyle = { fontSize: '17px', fontWeight: 700 };

export default PortfoliosPage;